import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { AccordionVariant } from './accordion.types';

type DefaultAccordionHeaderStyleProps = {
  defaultHeader?: 'title' | 'subtitle';
  hasSubtitle?: boolean;
};

const defaultAccordionHeaderStyle = ({ defaultHeader, hasSubtitle }: DefaultAccordionHeaderStyleProps) => {
  if (defaultHeader === 'title') {
    return css`
      font-size: ${theme.fontSize(18)};
      font-weight: ${theme.font.weight.bold};
      margin-bottom: ${hasSubtitle ? theme.spacing(0.5) : 0};
    `;
  }

  if (defaultHeader === 'subtitle') {
    return css`
      margin: 0;
      max-width: 500px;
      font-size: ${theme.fontSize(14)};
      color: ${theme.colors.neutral40};
    `;
  }
  return;
};

const getLocationBackgroundColor = (hasMultiLocations?: boolean) =>
  hasMultiLocations ? theme.colors.warning5 : theme.colors.primary20;
const getLocationFontColor = (hasMultiLocations?: boolean) =>
  hasMultiLocations ? theme.colors.warning80 : theme.colors.primary70;

type AccordionHeaderStyleProps = {
  chevronPosition?: 'left' | 'right';
  isActive?: boolean;
  variant?: AccordionVariant;
  isInteractive?: boolean;
  isDisabled?: boolean;
  hasMultiLocations?: boolean;
  showBorderBottomAlways?: boolean;
};

const accordionHeaderStyle = ({
  chevronPosition,
  isActive,
  variant,
  isInteractive,
  isDisabled,
  hasMultiLocations,
  showBorderBottomAlways,
}: AccordionHeaderStyleProps) => {
  const radius =
    variant === 'hybrid' ? `${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0` : theme.borderRadius.medium;
  let backgroundColor;
  let borderBottom = `1px solid ${isActive || showBorderBottomAlways ? theme.colors.neutral20 : 'transparent'}`;
  let transition = 'border 0.3s ease';
  let border = 'none';

  switch (variant) {
    case 'error':
      backgroundColor = isActive ? 'white' : theme.colors.critical5;
      break;
    case 'hybrid':
      backgroundColor = isActive ? 'white' : theme.colors.neutral5;
      transition = 'background 0.3s ease';
      border = isActive ? `1px solid ${theme.colors.neutral20}` : `1px solid ${theme.colors.neutral5}`;
      borderBottom = showBorderBottomAlways && isActive ? borderBottom : 'none';
      break;
    case 'filled':
      backgroundColor = theme.colors.neutral5;
      break;
    case 'location':
      backgroundColor = isActive ? getLocationBackgroundColor(hasMultiLocations) : 'white';
      transition = 'background 0.3s ease';
      borderBottom = 'none';
      break;
    case 'main':
      backgroundColor = 'white';
      transition = 'background 0.3s ease';
      borderBottom = 'none';
      break;
    default:
      backgroundColor = 'white';
  }

  return [
    css`
      display: flex;
      position: relative;
      flex-direction: ${chevronPosition === 'right' ? 'row-reverse' : 'row'};
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: space-between;
      border: ${border};
      border-bottom: ${borderBottom};
      background-color: ${backgroundColor};
      box-sizing: border-box;
      transition: ${transition};
    `,
    variant === 'error' &&
      css`
        span {
          color: ${theme.colors.status.critical};
        }
      `,
    variant === 'hybrid' &&
      css`
        border-radius: ${radius};
      `,
    variant === 'location' &&
      isActive &&
      css`
        color: ${getLocationFontColor(hasMultiLocations)};
        svg {
          color: ${getLocationFontColor(hasMultiLocations)};
        }
      `,
    variant === 'main' &&
      isActive &&
      css`
        background-color: ${theme.colors.neutral5};
      `,
    isDisabled &&
      css`
        cursor: unset;
        span {
          color: ${theme.colors.neutral20};
        }
      `,
    !isInteractive &&
      css`
        ::before {
          content: '';
          position: absolute;
          pointer-events: none;
          top: 0px;
          left: 0px;
          border: 2px solid ${theme.colors.primary50};
          border-radius: ${theme.borderRadius.medium};
          width: 100%;
          height: 100%;
          z-index: ${theme.zIndex.high};
          opacity: 0;
          transition: 0.2s ease;
        }
        :focus-visible {
          outline: none;
          ::before {
            opacity: 1;
          }
        }
      `,
  ];
};

type AccordionItemStyleProps = { variant?: AccordionVariant; distance?: number; showBoxShadow?: boolean };

const accordionItemStyle = ({ variant, distance, showBoxShadow }: AccordionItemStyleProps) => {
  const defaultStyles = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `;

  switch (variant) {
    case 'error':
      return [
        defaultStyles,
        css`
          margin-bottom: ${theme.spacing(distance ?? 2)};
          border-radius: ${theme.borderRadius.medium};
          border: 1px solid ${theme.colors.status.critical};
        `,
        showBoxShadow &&
          css`
            box-shadow: ${theme.shadows.light};
          `,
      ];

    case 'location':
    case 'hybrid': {
      return [
        defaultStyles,
        css`
          margin-bottom: ${theme.spacing(distance ?? 2)};
          border-radius: ${theme.borderRadius.medium};
        `,
        showBoxShadow &&
          css`
            box-shadow: ${theme.shadows.light};
          `,
      ];
    }

    case 'main': {
      return [
        defaultStyles,
        css`
          margin-bottom: ${theme.spacing(distance ?? 2)};
          border-radius: ${theme.borderRadius.medium};
          border: 1px solid ${theme.colors.neutral20};
        `,
        showBoxShadow &&
          css`
            box-shadow: ${theme.shadows.light};
          `,
      ];
    }

    case 'bordered': {
      return [
        defaultStyles,
        css`
          border: 1px solid ${theme.colors.neutral20};
          :first-child {
            border-radius: ${theme.spacing(1, 1, 0, 0)};
            border-bottom: none;
          }
          :last-child {
            border-radius: ${theme.spacing(0, 0, 1, 1)};
            border-top: none;
          }
        `,
      ];
    }

    case 'blank':
    case 'filled': {
      return css`
        width: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-bottom: 1px solid ${theme.colors.neutral20};

        :last-child {
          border-bottom: none;
        }
      `;
    }

    default: {
      return [
        defaultStyles,
        showBoxShadow === false
          ? null
          : css`
              box-shadow: ${theme.shadows.light};
            `,
        css`
          margin-bottom: ${theme.spacing(distance ?? 1.5)};
          border-radius: ${theme.spacing(1)};
        `,
      ];
    }
  }
};

type AccordionBodyStyleProps = Pick<AccordionItemStyleProps, 'variant'>;

const accordionBodyStyle = ({ variant }: AccordionBodyStyleProps) => {
  switch (variant) {
    case 'hybrid':
      return css`
        border-radius: 0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium};
        border: 1px solid ${theme.colors.neutral20};
        border-top: none;
      `;
    case 'location': {
      return css`
        background: white;
      `;
    }
    default:
      return;
  }
};

type ChevronContainerStyleProps = { disabled?: boolean };

export const chevronContainerStyle = ({ disabled }: ChevronContainerStyleProps) => css`
  height: ${theme.spacing(5)};
  padding: ${theme.spacing(1)};
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid transparent;
  ${!!disabled &&
  `svg {
    color: ${theme.colors.neutral20};
  }`}
`;

export const AccordionStyles = {
  defaultAccordionHeader: (defaultAccordionHeaderProps: DefaultAccordionHeaderStyleProps) =>
    defaultAccordionHeaderStyle(defaultAccordionHeaderProps),
  accordionHeader: (accordionHeaderProps: AccordionHeaderStyleProps) => accordionHeaderStyle(accordionHeaderProps),
  accordionBody: (accordionBodyProps: AccordionBodyStyleProps) => accordionBodyStyle(accordionBodyProps),
  accordionItem: (accordionItemProps: AccordionItemStyleProps) => accordionItemStyle(accordionItemProps),
  chevronContainer: (chevronContainerProps: ChevronContainerStyleProps) => chevronContainerStyle(chevronContainerProps),
};
